import React from 'react';
import { useTranslation } from 'react-i18next';

import { FormLayout } from '@/components/layout';
import { LabelInput } from '@/components/ui/inputs/LabelInput';
import { Selector } from '@/components/ui/inputs/selector/Selector';
import type { SelectorOption } from '@/components/ui/inputs/selector/Selector.types';
import type { ScheduleFormSubset } from '@/schemas/schedule/scheduleForm.schema';


interface ScheduleGeneralFormProps {
  form: ScheduleFormSubset;
  timezoneOptions: SelectorOption[];
  shiftGroupOptions: SelectorOption[];
  isTwoDayScheduleOptions: SelectorOption[];
  isEditMode: boolean;
  scheduleId?: number;
}

const handleSubmit = (event: React.FormEvent) => {
  event.preventDefault();
};

export const ScheduleGeneralForm: React.FC<ScheduleGeneralFormProps> = ({
  form,
  timezoneOptions,
  shiftGroupOptions,
  isTwoDayScheduleOptions,
  isEditMode,
  scheduleId,
}) => {
  const { t: translate } = useTranslation('schedule');
  const { register, watch, setValue, errors } = form;

  return (
    <FormLayout onSubmit={handleSubmit} columns={4} data-testid="schedule-general-form">
      <FormLayout.Section title={translate('form.sections.generalData')}>
        {/* ID — solo lectura, visible únicamente en modo edición */}
        {isEditMode && (
          <LabelInput
            label={translate('form.fields.id')}
            value={String(scheduleId ?? '')}
            readOnly
            data-testid="schedule-id-input"
          />
        )}

        {/* Nombre */}
        <LabelInput
          label={translate('form.fields.name')}
          required
          registration={register('name')}
          color={errors.name ? 'error' : 'default'}
          supportingText={errors.name?.message ? translate(errors.name.message) : undefined}
          data-testid="schedule-name-input"
        />

        {/* Horario dos días */}
        <Selector
          label={translate('form.fields.isTwoDaySchedule')}
          required
          options={isTwoDayScheduleOptions}
          value={watch('isTwoDaySchedule') !== undefined ? String(watch('isTwoDaySchedule')) : undefined}
          onChange={(value) => {
            const selected = Array.isArray(value) ? value[0] : value;
            setValue('isTwoDaySchedule', selected === 'true', { shouldDirty: true, shouldValidate: true });
          }}
          color={errors.isTwoDaySchedule ? 'error' : 'default'}
          supportingText={
            errors.isTwoDaySchedule?.message
              ? translate(errors.isTwoDaySchedule.message)
              : undefined
          }
          data-testid="schedule-two-day-selector"
        />

        {/* Zona horaria */}
        <Selector
          label={translate('form.fields.timeZone')}
          required
          options={timezoneOptions}
          value={watch('timeZoneId') !== undefined ? String(watch('timeZoneId')) : undefined}
          onChange={(value) => {
            const selected = Array.isArray(value) ? value[0] : value;
            setValue('timeZoneId', selected as unknown as number, { shouldDirty: true, shouldValidate: true });
          }}
          color={errors.timeZoneId ? 'error' : 'default'}
          supportingText={
            errors.timeZoneId?.message ? translate(errors.timeZoneId.message) : undefined
          }
          data-testid="schedule-timezone-selector"
        />

        {/* Grupo de turno */}
        <Selector
          label={translate('form.fields.shiftGroup')}
          required
          options={shiftGroupOptions}
          value={watch('shiftGroupId') !== undefined ? String(watch('shiftGroupId')) : undefined}
          onChange={(value) => {
            const selected = Array.isArray(value) ? value[0] : value;
            setValue('shiftGroupId', selected as unknown as number, { shouldDirty: true, shouldValidate: true });
          }}
          color={errors.shiftGroupId ? 'error' : 'default'}
          supportingText={
            errors.shiftGroupId?.message ? translate(errors.shiftGroupId.message) : undefined
          }
          data-testid="schedule-shift-group-selector"
        />
      </FormLayout.Section>
    </FormLayout>
  );
};
